import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface DesignStage {
  name: string;
  nameEn: string;
  icon: string;
  desc: string;
  color: string;
}

interface UXPrinciple {
  title: string;
  icon: string;
  detail: string;
}

interface DesignTool {
  name: string;
  category: string;
  color: string;
}

interface UserPersona {
  role: string;
  pain: string;
  goal: string;
  icon: string;
}

interface ProductDesignTemplateProps {
  topic: string;
  subtitle: string;
  designStages: DesignStage[];
  uxPrinciples: UXPrinciple[];
  tools: DesignTool[];
  personas: UserPersona[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: ProductDesignTemplateProps = {
  topic: '产品设计方法论',
  subtitle: 'Product Design Methodology & UX',
  designStages: [
    { name: '共情', nameEn: 'Empathize', icon: '❤️', desc: '深入了解用户需求与痛点', color: '#ef4444' },
    { name: '定义', nameEn: 'Define', icon: '🎯', desc: '明确核心问题与设计目标', color: '#f59e0b' },
    { name: '构思', nameEn: 'Ideate', icon: '💡', desc: '头脑风暴产出创意方案', color: '#22c55e' },
    { name: '原型', nameEn: 'Prototype', icon: '🔧', desc: '快速构建可测试的最小原型', color: '#3b82f6' },
    { name: '测试', nameEn: 'Test', icon: '🧪', desc: '用户测试验证并迭代优化', color: '#8b5cf6' },
  ],
  uxPrinciples: [
    { title: '可用性', icon: '✅', detail: '界面易学易用，降低认知负荷' },
    { title: '一致性', icon: '🔄', detail: '视觉与交互风格保持统一' },
    { title: '反馈性', icon: '💬', detail: '每个操作都有即时可感知的响应' },
    { title: '容错性', icon: '🛡️', detail: '允许犯错并提供简便的恢复路径' },
  ],
  tools: [
    { name: 'Figma', category: '界面设计', color: '#a259ff' },
    { name: 'Notion', category: '需求管理', color: '#e6e6e4' },
    { name: 'Maze', category: '用户测试', color: '#7b61ff' },
    { name: 'Miro', category: '协作白板', color: '#ffd02f' },
    { name: 'Framer', category: '原型交互', color: '#0055ff' },
    { name: 'Hotjar', category: '行为分析', color: '#ff3c00' },
  ],
  personas: [
    { role: '新手用户', pain: '界面复杂难以上手', goal: '5分钟完成核心任务', icon: '🙋' },
    { role: '高频用户', pain: '重复操作效率低', goal: '快捷键与批量操作', icon: '⚡' },
  ],
  stats: [
    { value: '5 步', label: 'Design Thinking 流程' },
    { value: '10x', label: '好设计的投资回报率' },
    { value: '88%', label: '用户因体验差而流失' },
  ],
  bottomQuote: '好的设计是尽可能少的设计——少即是多，专注于真正重要的事 —— Dieter Rams',
};

export const ProductDesignTemplate: React.FC<
  Partial<ProductDesignTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const stageStart = 50;
  const diamondStart = 40;
  const uxStart = 200;
  const toolStart = 300;
  const personaStart = 380;
  const statsStart = 450;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const diamondDraw = interpolate(frame, [diamondStart, diamondStart + 90], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(155deg, #0a0a12 0%, #12121e 50%, #0e0e1a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 40 }).map((_, i) => {
        const x = (i * 229) % 1920;
        const y = (i * 181) % 1080;
        const size = 1.5 + (i % 3);
        return (
          <div
            key={`dot-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#8b5cf6',
              opacity: 0.06,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 18}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#1e1b3a',
            borderRadius: 8,
            fontSize: 20,
            fontWeight: 700,
            color: '#c084fc',
            flexShrink: 0,
            border: '1px solid #c084fc30',
          }}
        >
          🎨 职业技能
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 900, margin: 0, color: '#f1f5f9' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT: Double Diamond SVG ===== */}
      <svg
        style={{ position: 'absolute', left: 30, top: 150 }}
        width="560"
        height="260"
        viewBox="0 0 560 260"
      >
        <text x="280" y="20" fill="#64748b" fontSize="26" textAnchor="middle" fontWeight="600">
          双钻石模型 · Double Diamond
        </text>

        {/* First diamond - Discover & Define */}
        <path
          d="M40,130 L180,40 L320,130 L180,220 Z"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="2.5"
          strokeDasharray="700"
          strokeDashoffset={700 * (1 - Math.min(1, diamondDraw * 2))}
          opacity="0.8"
        />
        <path
          d="M40,130 L180,40 L320,130 L180,220 Z"
          fill="#f59e0b08"
          opacity={interpolate(diamondDraw, [0.3, 0.5], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        />

        {/* Second diamond - Develop & Deliver */}
        <path
          d="M240,130 L380,40 L520,130 L380,220 Z"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="2.5"
          strokeDasharray="700"
          strokeDashoffset={700 * (1 - Math.max(0, diamondDraw * 2 - 1))}
          opacity="0.8"
        />
        <path
          d="M240,130 L380,40 L520,130 L380,220 Z"
          fill="#8b5cf608"
          opacity={interpolate(diamondDraw, [0.7, 0.9], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}
        />

        {/* Labels */}
        <text x="110" y="135" fill="#f59e0b" fontSize="22" textAnchor="middle" fontWeight="700"
          opacity={interpolate(diamondDraw, [0.2, 0.4], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >发散</text>
        <text x="250" y="135" fill="#f59e0b" fontSize="22" textAnchor="middle" fontWeight="700"
          opacity={interpolate(diamondDraw, [0.35, 0.55], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >收敛</text>
        <text x="310" y="135" fill="#8b5cf6" fontSize="22" textAnchor="middle" fontWeight="700"
          opacity={interpolate(diamondDraw, [0.5, 0.7], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >发散</text>
        <text x="450" y="135" fill="#8b5cf6" fontSize="22" textAnchor="middle" fontWeight="700"
          opacity={interpolate(diamondDraw, [0.7, 0.9], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >收敛</text>

        <text x="180" y="250" fill="#64748b" fontSize="22" textAnchor="middle"
          opacity={interpolate(diamondDraw, [0.4, 0.6], [0, 0.8], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >探索问题空间</text>
        <text x="380" y="250" fill="#64748b" fontSize="22" textAnchor="middle"
          opacity={interpolate(diamondDraw, [0.7, 0.9], [0, 0.8], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >探索方案空间</text>
      </svg>

      {/* ===== Design Thinking 5 Stages (horizontal flow) ===== */}
      <div style={{ position: 'absolute', top: 440, left: 40, right: 40 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          设计思维五步法 · Design Thinking
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {p.designStages.map((stage, i) => {
            const s = spring({
              frame: frame - (stageStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const isLast = i === p.designStages.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    opacity: s,
                    transform: `translateY(${(1 - s) * 20}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      background: `${stage.color}18`,
                      border: `2.5px solid ${stage.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                      margin: '0 auto',
                    }}
                  >
                    {stage.icon}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: stage.color, marginTop: 8 }}>
                    {stage.name}
                  </div>
                  <div style={{ fontSize: 20, color: '#64748b', fontWeight: 600 }}>
                    {stage.nameEn}
                  </div>
                  <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                    {stage.desc}
                  </div>
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 40,
                      height: 2,
                      background: `linear-gradient(90deg, ${stage.color}, ${p.designStages[i + 1].color})`,
                      marginTop: 32,
                      flexShrink: 0,
                      opacity: interpolate(frame, [stageStart + i * 18 + 10, stageStart + i * 18 + 25], [0, 0.6], {
                        extrapolateLeft: 'clamp',
                        extrapolateRight: 'clamp',
                      }),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== RIGHT TOP: UX Principles ===== */}
      <div style={{ position: 'absolute', top: 120, right: 40, width: 440 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          用户体验设计原则
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {p.uxPrinciples.map((pr, i) => {
            const s = spring({
              frame: frame - (uxStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const colors = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'];
            return (
              <div
                key={i}
                style={{
                  width: 'calc(50% - 5px)',
                  background: '#0f0f23',
                  borderRadius: 12,
                  padding: '14px 14px',
                  border: `1px solid ${colors[i]}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 24 }}>{pr.icon}</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: colors[i] }}>{pr.title}</span>
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                  {pr.detail}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== BOTTOM LEFT: User Personas ===== */}
      <div style={{ position: 'absolute', top: 700, left: 40, width: 550 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          用户画像
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          {p.personas.map((persona, i) => {
            const s = spring({
              frame: frame - (personaStart + i * 20),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: '#0f0f23',
                  borderRadius: 14,
                  padding: '16px 14px',
                  border: '1px solid #1e293b',
                  opacity: s,
                  transform: `translateY(${(1 - s) * 20}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 32, lineHeight: 1 }}>{persona.icon}</span>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#c084fc' }}>{persona.role}</span>
                </div>
                <div style={{ fontSize: 20, color: '#ef4444', marginTop: 6 }}>
                  痛点：{persona.pain}
                </div>
                <div style={{ fontSize: 20, color: '#22c55e', marginTop: 4 }}>
                  目标：{persona.goal}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== BOTTOM RIGHT: Design Tools ===== */}
      <div style={{ position: 'absolute', top: 700, right: 40, width: 600 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          设计工具箱
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {p.tools.map((tool, i) => {
            const s = spring({
              frame: frame - (toolStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(33.33% - 7px)',
                  background: '#0f0f23',
                  borderRadius: 10,
                  padding: '12px 14px',
                  textAlign: 'center',
                  border: `1.5px solid ${tool.color}30`,
                  opacity: s,
                  transform: `scale(${0.8 + 0.2 * s})`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    flexWrap: 'nowrap',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ fontSize: 24, fontWeight: 800, color: tool.color }}>{tool.name}</span>
                  <span style={{ fontSize: 22, color: '#94a3b8' }}>{tool.category}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 95,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f0f23',
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e293b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 30, fontWeight: 900, color: '#c084fc' }}>{st.value}</span>
                <span style={{ fontSize: 22, color: '#64748b' }}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 40,
          right: 40,
          background: '#0f0f23',
          borderRadius: 14,
          padding: '14px 20px',
          borderTop: '3px solid #c084fc',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🎨</span>
          <span style={{ fontSize: 20, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomQuote}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #ef4444, #f59e0b, #22c55e, #3b82f6, #8b5cf6)',
        }}
      />
    </AbsoluteFill>
  );
};
